import * as _ from 'lodash';
// tslint:disable-next-line:no-submodule-imports
import { all, call, Effect, put, putResolve, select } from 'redux-saga/effects';
import { Parent, Workspace, WorkspaceCollection } from '../../clients/aasServerClient.generated';
import * as WorkspaceRepository from '../../repositories/WorkspaceRepository';
import { takeEveryWithErrorHandling } from '../sagas';
import { RootState } from '../store';
import { storePagingState } from '../tables/actionCreators';
import { unselectAll} from '../tables/actionCreators';
import { getFilters, getLimit, getPaging, getSearch, getSorting, getStart } from '../tables/selectors';
import { TableFilters, TablePaging, TableSorting } from '../tables/state';
import {
    fetchAllWorkspaces as fetchAllWorkspacesCreator,
    fetchTableWorkspaces as fetchWorkspacesCreator,
    savedSingleWorkspace,
    storeAllWorkspaces,
    storeSingleWorkspace,
    storeTableWorkspaces,
} from './actionCreators';
import {
    ActionTypeNames, AssignApplicationsWorkspaceAction,
    AssignGroupsWorkspacesAction, CopySingleWorkspaceAction, CreateSingleWorkspaceAction,
    DeleteWorkspacesAction, FetchSingleWorkspaceAction, NewSingleWorkspaceAction,
    SaveSingleWorkspaceAction,
} from './actions';

function* fetchTableWorkspaces() {
    const start: number = yield select(getStart, 'workspaces');
    const limit: number = yield select(getLimit, 'workspaces');
    const sorting: TableSorting = yield select(getSorting, 'workspaces');
    const filters: TableFilters = yield select(getFilters, 'workspaces');
    const paging: TablePaging = yield select(getPaging, 'workspaces');
    const search: string = yield select(getSearch, 'workspaces');

    const res: WorkspaceCollection = yield call(
        WorkspaceRepository.getWorkspaces,
        limit, start, sorting,
        filters, search);
    yield all([
        putResolve(storeTableWorkspaces(res.items)),
        putResolve(storePagingState('workspaces', {
            numberOfItems: res.count || 0,
            numberOfPages: Math.ceil((res.count || 0) / paging.itemsPerPage)
        }))
    ]);
}

function* fetchTableWorkspacesNF() {
    const start: number = yield select(getStart, 'workspaces');
    const limit: number = yield select(getLimit, 'workspaces');
    const sorting: TableSorting = yield select(getSorting, 'workspaces');
    const filters: TableFilters = yield select(getFilters, 'workspaces');
    const paging: TablePaging = yield select(getPaging, 'workspaces');
    const search: string = yield select(getSearch, 'workspaces');

    const res: WorkspaceCollection = yield call(
        WorkspaceRepository.getWorkspacesNF,
        limit, start, sorting,
         );
    yield all([
        putResolve(storeTableWorkspaces(res.items)),
        putResolve(storePagingState('workspaces', {
            numberOfItems: res.count || 0,
            numberOfPages: Math.ceil((res.count || 0) / paging.itemsPerPage)
        }))
    ]);
}


export function* watchFetchTableWorkspaces() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchTableWorkspaces, fetchTableWorkspaces);
}

export function* watchFetchTableWorkspacesNF() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchTableWorkspacesNF, fetchTableWorkspacesNF);
}

function* fetchAllWorkspaces() {
    const res: WorkspaceCollection = yield call(WorkspaceRepository.getAllWorkspaces);
    yield putResolve(storeAllWorkspaces(res.items));
}

export function* watchFetchAllWorkspaces() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchAllWorkspaces, fetchAllWorkspaces);
}

function* fetchSingleWorkspace(action: FetchSingleWorkspaceAction) {
    const workspace: Workspace = yield call(WorkspaceRepository.getWorkspace, action.id);
    delete workspace.users;
    delete workspace.applications;
    yield putResolve(storeSingleWorkspace(workspace));
}

function* watchFetchSingleWorkspace() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchSingleWorkspace, fetchSingleWorkspace);
}

function* copySingleWorkspace(action: CopySingleWorkspaceAction) {
    const res: Workspace = yield call(WorkspaceRepository.getWorkspace, action.id);
    const workspace: Partial<Workspace> = {
        accent_color: res.accent_color,
        background_img: res.background_img,
        host: res.host,
        icon_img: res.icon_img,
        ip_restricted: res.ip_restricted,
        logo_img: res.logo_img,
        name: '',
        org_name: res.org_name,
        parent: res.parent,
        user_limit: undefined,

        sso_callback: res.sso_callback,
        sso_entrypoint: res.sso_entrypoint,
        sso_issuer: res.sso_issuer,
        sso_logout: res.sso_logout,
        sso_logoutcallback: res.sso_logoutcallback,
        sso_certificate: res.sso_certificate,
        sso_private_key: res.sso_private_key,
        sso_public_key: res.sso_public_key,

        mail_template: res.mail_template,
        etl_url: res.etl_url,
        groups: res.groups,
    };
    yield putResolve(storeSingleWorkspace(workspace));
}

function* watchCopySingleWorkspace() {
    yield takeEveryWithErrorHandling(ActionTypeNames.CopySingleWorkspace, copySingleWorkspace);
}

function* newSingleWorkspace(action: NewSingleWorkspaceAction) {
    const workspace: Partial<Workspace> = {
        accent_color: '',
        background_img: '',
        host: '',
        icon_img: '',
        ip_restricted: false,
        logo_img: '',
        name: '',
        org_name: '',
        parent: new Parent({ id: '' }),
        user_limit: undefined,

        sso_callback: '',
        sso_entrypoint: '',
        sso_issuer: '',
        sso_logout: '',
        sso_logoutcallback: '',
        sso_certificate: '',
        sso_private_key: '',
        sso_public_key: '',

        mail_template: '',
        etl_url: '',
        groups: [],
    };
    yield putResolve(storeSingleWorkspace(workspace));
}

function* watchNewSingleWorkspace() {
    yield takeEveryWithErrorHandling(ActionTypeNames.NewSingleWorkspace, newSingleWorkspace);
}

function* saveSingleWorkspace(action: SaveSingleWorkspaceAction) {
    const res = yield call(
        WorkspaceRepository.updateWorkspace,
        action.workspace, action.parentId, action.groups,
    );
    yield putResolve(savedSingleWorkspace());
    yield all([
        put(fetchWorkspacesCreator()),
        put(fetchAllWorkspacesCreator()),
    ]);
}

function* watchSaveSingleWorkspace() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SaveSingleWorkspace, saveSingleWorkspace);
}

function* createSingleWorkspace(action: CreateSingleWorkspaceAction) {
    const res = yield call(
        WorkspaceRepository.createWorkspace,
        action.workspace,
        action.parentId,
        action.groups
    );
    yield putResolve(savedSingleWorkspace());
    yield all([
        put(fetchWorkspacesCreator()),
        put(fetchAllWorkspacesCreator()),
    ]);
}

function* watchCreateSingleWorkspace() {
    yield takeEveryWithErrorHandling(ActionTypeNames.CreateSingleWorkspace, createSingleWorkspace);
}

function* watchDeleteWorkspaces() {
    yield takeEveryWithErrorHandling(ActionTypeNames.DeleteWorkspaces, deleteWorkspaces);
}

function* deleteWorkspaces(action: DeleteWorkspacesAction) {
    yield call(WorkspaceRepository.deleteWorkspaces, action.workspaceIds);
    yield put(unselectAll('workspaces',action.workspaceIds)) ;        
    yield putResolve(fetchWorkspacesCreator());
}

function* watchAssignGroupsWorkspaces() {
    yield takeEveryWithErrorHandling(ActionTypeNames.AssignGroupsWorkspaces, assignGroupsWorkspaces);
}

function* assignGroupsWorkspaces(action: AssignGroupsWorkspacesAction) {
    yield call(WorkspaceRepository.assignGroupsWorkspaces, action.workspaceIds, action.groupIds);
    yield putResolve(fetchWorkspacesCreator());
}

function* watchAssignApplicationsWorkspace() {
    yield takeEveryWithErrorHandling(ActionTypeNames.AssignApplicationsWorkspace, assignApplicationsWorkspace);
}

function* assignApplicationsWorkspace(action: AssignApplicationsWorkspaceAction) {
    yield call(WorkspaceRepository.assignApplicationsWorkspace, action.workspaceId, action.applicationIds);
    yield putResolve(fetchWorkspacesCreator());
}

export default [watchFetchTableWorkspaces,watchFetchTableWorkspacesNF, watchFetchAllWorkspaces,
    watchDeleteWorkspaces, watchAssignApplicationsWorkspace, watchAssignGroupsWorkspaces,
    watchCopySingleWorkspace, watchFetchSingleWorkspace, watchSaveSingleWorkspace,
    watchCreateSingleWorkspace, watchNewSingleWorkspace];